import { BRAND, ENVIRONMENT } from '../utils/environment';
import { getErrorMessage } from '../utils/errors';
import { sanitizeLogArgs } from '../utils/sanitizeLogArgs';

import { getBrowserSessionId } from './analytics/getBrowserSessionId';
import { getTabId } from './analytics/getTabId';
import { postRequest } from './api';
import { resolveFlag } from './launchDarkly';

const LOGGED_PROCESS_NAME_KEY = 'logged_process_name';
const LOGGED_PROCESS_ID_KEY = 'logged_process_id';

// error/fatal/warn always forwarded; info/debug can be sampled via the flag
// (default 1.0 = send all). It's an ingest/agent-flood dial, not a cost lever.
const ALWAYS_SENT_LEVELS = new Set(['error', 'fatal', 'warn']);

function shouldForwardLog(method: 'fatal' | 'info' | 'debug' | 'error' | 'warn') {
  if (ALWAYS_SENT_LEVELS.has(method)) {
    return true;
  }

  const sampleRate = resolveFlag<number>('client_debug_log_sample_rate', 1);

  if (sampleRate >= 1) {
    return true;
  }

  if (sampleRate <= 0) {
    return false;
  }

  return Math.random() < sampleRate;
}

function generateId() {
  if (typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${(Math.random() * 100).toFixed(0)}`;
}

export function startLoggedProcess(name: string) {
  try {
    localStorage.setItem(LOGGED_PROCESS_NAME_KEY, name);
    localStorage.setItem(LOGGED_PROCESS_ID_KEY, generateId());
  } catch {}
}

export function endLoggedProcess() {
  try {
    localStorage.removeItem(LOGGED_PROCESS_NAME_KEY);
    localStorage.removeItem(LOGGED_PROCESS_ID_KEY);
  } catch {}
}

export const logToNewRelic = (
  title: string,
  message: string | Record<string, any>,
  method: 'fatal' | 'info' | 'debug' | 'error' | 'warn' = 'info',
  context?: Record<string, any>,
) => {
  try {
    if (!shouldForwardLog(method)) {
      return;
    }

    const browserSessionId = getBrowserSessionId();
    const tabId = getTabId();

    const fullContext = { ...(context || {}) };

    try {
      const loggedProcessName = localStorage.getItem(LOGGED_PROCESS_NAME_KEY);
      const loggedProcessId = localStorage.getItem(LOGGED_PROCESS_ID_KEY);

      if (loggedProcessId && loggedProcessName) {
        fullContext.ongoingProcess = {
          name: loggedProcessName,
          traceId: loggedProcessId,
        };
      }
    } catch {}

    const prefixedTitle = `Players-Client: ${title}`;
    const messageText = typeof message === 'string' ? message : JSON.stringify(message);

    // Prefer the NR browser agent — posts direct to New Relic, off the CDN.
    if (typeof window !== 'undefined' && window.newrelic) {
      const customAttributes = sanitizeLogArgs({
        ...fullContext,
        title: prefixedTitle,
        brand: BRAND,
        env: ENVIRONMENT,
        browserSessionId,
        tabId,
      }) as Record<string, unknown>;

      const level = method === 'fatal' ? 'error' : method; // fatal isn't a NR level
      window.newrelic.log(`${prefixedTitle}: ${messageText}`, { level, customAttributes });

      if (method === 'error' || method === 'fatal') {
        window.newrelic.noticeError(new Error(prefixedTitle), customAttributes);
      }

      return;
    }

    // Fallback when the agent is unavailable: the legacy CDN-billed POST.
    void postRequest('/v2/debug/log', {
      title: prefixedTitle,
      message:
        typeof message === 'string'
          ? message + ` [Browser Session ID: ${browserSessionId}] [Tab ID: ${tabId}]`
          : {
              ...message,
              browserSessionId,
              tabId,
            },
      method,
      context: fullContext,
    });
  } catch (e) {
    const errorMessage = getErrorMessage(e);
    console.error(`Error sending log: ${errorMessage}`);
  }
};
